import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  AnalyticsSummary,
  Bot,
  ModuleConfigRead,
  WabaOption,
  WhatsAppConnection,
} from "@/types/api";

export interface BotInput {
  name: string;
  language: string;
  timezone: string;
}

async function listBots(): Promise<Bot[]> {
  const { data } = await api.get<Bot[]>("/api/v1/bots");
  return data;
}

async function createBot(input: BotInput): Promise<Bot> {
  const { data } = await api.post<Bot>("/api/v1/bots", input);
  return data;
}

async function deleteBot(botId: string): Promise<void> {
  await api.delete(`/api/v1/bots/${botId}`);
}

async function getBot(botId: string): Promise<Bot> {
  const { data } = await api.get<Bot>(`/api/v1/bots/${botId}`);
  return data;
}

async function listBotModules(botId: string): Promise<ModuleConfigRead[]> {
  const { data } = await api.get<ModuleConfigRead[]>(`/api/v1/modules/bots/${botId}/config`);
  return data;
}

export interface ModuleConfigInput {
  enabled: boolean;
  configuration: Record<string, unknown>;
}

async function upsertModuleConfig(
  botId: string,
  moduleCode: string,
  input: ModuleConfigInput,
): Promise<ModuleConfigRead> {
  const { data } = await api.put<ModuleConfigRead>(
    `/api/v1/modules/bots/${botId}/config/${moduleCode}`,
    input,
  );
  return data;
}

async function getConnections(): Promise<WhatsAppConnection[]> {
  const { data } = await api.get<{ connections: WhatsAppConnection[] }>("/api/v1/whatsapp/connection");
  return data.connections;
}

async function startOnboarding(botId: string): Promise<string> {
  const { data } = await api.post<{ consent_url: string }>("/api/v1/whatsapp/onboard", {
    bot_id: botId,
  });
  return data.consent_url;
}

async function disconnectBot(botId: string): Promise<void> {
  await api.post("/api/v1/whatsapp/disconnect", { bot_id: botId });
}

async function getConnectionOptions(botId: string): Promise<WabaOption[]> {
  const { data } = await api.get<{ bot_id: string; wabas: WabaOption[] }>(
    "/api/v1/whatsapp/options",
    { params: { bot_id: botId } },
  );
  return data.wabas;
}

interface SelectNumberInput {
  botId: string;
  wabaId: string;
  phoneNumberId: string;
}

async function selectNumber({ botId, wabaId, phoneNumberId }: SelectNumberInput): Promise<void> {
  await api.post("/api/v1/whatsapp/select", {
    bot_id: botId,
    waba_id: wabaId,
    phone_number_id: phoneNumberId,
  });
}

async function getAnalytics(): Promise<AnalyticsSummary> {
  const { data } = await api.get<AnalyticsSummary>("/api/v1/analytics/summary");
  return data;
}

export function useBots() {
  return useQuery({ queryKey: ["bots"], queryFn: listBots });
}

export function useBot(botId: string) {
  return useQuery({ queryKey: ["bots", botId], queryFn: () => getBot(botId), enabled: Boolean(botId) });
}

export function useCreateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBot,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bots"] }),
  });
}

export function useDeleteBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteBot,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bots"] }),
  });
}

export function useBotModules(botId: string) {
  return useQuery({
    queryKey: ["bots", botId, "modules"],
    queryFn: () => listBotModules(botId),
    enabled: Boolean(botId),
  });
}

export function useUpsertModuleConfig(botId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleCode, input }: { moduleCode: string; input: ModuleConfigInput }) =>
      upsertModuleConfig(botId, moduleCode, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bots", botId, "modules"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    },
  });
}

export function useConnections() {
  return useQuery({ queryKey: ["connections"], queryFn: getConnections });
}

export function useStartOnboarding() {
  return useMutation({ mutationFn: startOnboarding });
}

export function useDisconnectBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: disconnectBot,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      qc.invalidateQueries({ queryKey: ["bots"] });
    },
  });
}

export function useConnectionOptions(botId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["connections", botId, "options"],
    queryFn: () => getConnectionOptions(botId),
    enabled: enabled && Boolean(botId),
  });
}

export function useSelectNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: selectNumber,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export function useAnalytics() {
  return useQuery({ queryKey: ["analytics"], queryFn: getAnalytics });
}